import { Core } from "@faire/design-tokens";
import { ContinueToNextViewButton } from "@faire/retailer-visitor-shared/components/AppSignIn/components/ContinueToNextViewButton";
import { convertErrorToLoginError } from "@faire/retailer-visitor-shared/components/AppSignIn/components/convertErrorToLoginError";
import { ErrorNotValidEmail } from "@faire/retailer-visitor-shared/components/AppSignIn/components/ErrorMessage";
import { LoginError } from "@faire/retailer-visitor-shared/components/AppSignIn/components/LoginError";
import { LoginWrapper } from "@faire/retailer-visitor-shared/components/AppSignIn/components/styled";
import { View } from "@faire/retailer-visitor-shared/components/AppSignIn/components/View";
import { useSignInEventTracker } from "@faire/retailer-visitor-shared/components/AppSignIn/useSignInEventTracker";
import { AppSignUpModalVariant } from "@faire/retailer-visitor-shared/components/AppSignUp/AppSignUpModalVariant";
import { SocialSignOnButtons } from "@faire/retailer-visitor-shared/components/SocialSignOnButtons/index";
import { ssoCache } from "@faire/retailer-visitor-shared/features/SignUp/SSORequestData";
import { useFaireDirectBrand } from "@faire/retailer-visitor-shared/hooks/FaireDirect/useFaireDirectBrand/useFaireDirectBrand";
import { useFetchHCaptchaToken } from "@faire/retailer-visitor-shared/hooks/hCaptcha/useFetchHCaptchaToken";
import { handleKeyDown } from "@faire/retailer-visitor-shared/lib/handleKeyDown";
import {
  getPayloadForConfirmPassword,
  showSSOButtonsOnSignIn,
} from "@faire/retailer-visitor-shared/lib/ssoUtils";
import { isValidEmail } from "@faire/retailer-visitor-shared/lib/validation";
// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { useAssignSetting } from "@faire/retailer-visitor-shared/settings/__internal__/useSetting";
import { useAssignSettingBatch9DirectSignUpOnSignIn } from "@faire/retailer-visitor-shared/settings/useSettingBatch9DirectSignUpOnSignIn";
import { useSettingSignUpSplit } from "@faire/retailer-visitor-shared/settings/useSettingSignUpSplit";
import { useSSOErrorStore } from "@faire/retailer-visitor-shared/stores/domain/SSO/useSSOErrorStore";
import { useSSOPasswordStore } from "@faire/retailer-visitor-shared/stores/domain/SSO/useSSOPasswordStore";
import { useSSOProviderOptionsStore } from "@faire/retailer-visitor-shared/stores/domain/SSO/useSSOProviderOptionsStore";
import { useSSOStore } from "@faire/retailer-visitor-shared/stores/domain/SSO/useSSOStore";
import { emitDatadogError } from "@faire/web--source/common/datadog/rum";
import { getLocationOrThrow } from "@faire/web--source/common/globals/getLocation";
import {
  StrictLocalizeFunction,
  useStrictLocalization,
} from "@faire/web--source/common/localization/localize";
import { parseQueryParams } from "@faire/web--source/common/query-params/parseQueryParams";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { trackEvent } from "@faire/web--source/common/trackEvent";
import { Input } from "@faire/web--source/slate/Input";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { useQueryParams } from "@faire/web--source/ui/hooks/useQueryParams";
import { removeLocaleFromUrl } from "@faire/web--source/ui/routing/util";
import userLoginData from "@faire/web-api--source/endpoints/www/api/user/login-data/post";
import saveIncompleteSignupAttempt from "@faire/web-api--source/endpoints/www/api/user/retailer-signup-attempt/post";
import login from "@faire/web-api--source/endpoints/www/api/v2/users/login/post";
import { trackRetailerPortalLoginModalEnterEmail } from "@faire/web-api--source/events/retailerPortal/actionCategoryOther/loginModalEnterEmail";
import { trackSigninTriggersMfaSubmit } from "@faire/web-api--source/events/signin/submit/triggersMfa";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { ILoginDataRequest } from "@faire/web-api--source/indigofair/data/ILoginDataRequest";
import { ILoginRequest } from "@faire/web-api--source/indigofair/data/ILoginRequest";
import { ILoginResponse } from "@faire/web-api--source/indigofair/data/ILoginResponse";
import { ISignUpRequest } from "@faire/web-api--source/indigofair/data/ISignUpRequest";
import { ISignUpSuggestions } from "@faire/web-api--source/indigofair/data/ISignUpSuggestions";
import { IUser } from "@faire/web-api--source/indigofair/data/IUser";
import { SocialSignOnProvider } from "@faire/web-api--source/indigofair/data/SocialSignOnProvider";
import { NEXTJS_SIGNUP_SPLIT_250311 } from "@faire/web-api--source/indigofair/settings/NEXTJS_SIGNUP_SPLIT_250311";
import signUpPersonal, {
  QueryParameters as SignUpQueryParameters,
} from "@faire/web-api--source/routes/www/welcome/r/personal";
import React, { FC, useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled, { keyframes } from "styled-components";

import { buildFaireDirectParams } from "../../AppSignUp/utils/paramBuilders";

type HandleEmailSubmitResult = {
  nextView: View;
  userToken?: string;
  obfuscatedEmail?: string;
  email?: string;
  user?: IUser | null;
  mfaDescription?: string;
  mfaTitle?: string;
  mfaOptions?: ILoginResponse.IMfaOption[];
  profileImage?: IImage;
  userName?: string;
  isMFAFromEmail?: boolean;
};

type IProps = {
  onComplete: (result: HandleEmailSubmitResult) => void;
  onLogin: (user: IUser) => void;
  signInTitle: string;
  defaultEmail?: string;
  hideApple?: boolean;
  isResetPasswordSuccess?: boolean;
};

enum oktaError {
  not_assigned = "User not assigned to okta app, please use AccessBot to request a role",
  backend_error = "Failed to sign in with Okta, please try again using the okta button",
}

export const EnterEmailView: FC<IProps> = ({
  onComplete,
  onLogin,
  defaultEmail,
  signInTitle,
  hideApple,
  isResetPasswordSuccess,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const [email, setEmail] = useState<string | undefined>(defaultEmail);
  const [error, setError] = useState<LoginError>();
  const [isLoading, setIsLoading] = useState(false);
  const { fetchHCaptchaToken } = useFetchHCaptchaToken();
  const { trackFieldValue } = useSignInEventTracker("ENTER_EMAIL");
  const queryParams = parseQueryParams(useLocation().search);
  const oktaSignInError = queryParams.get("error");

  useEffect(() => {
    if (oktaSignInError === "USER_NOT_ASSIGNED") {
      setError(
        new LoginError(
          strictLocalize({
            defaultMessage: oktaError.not_assigned,
            description: {
              text: "user is not assign to okta app, they should use email and password combination",
            },
          })
        )
      );
    } else if (oktaSignInError) {
      setError(
        new LoginError(
          strictLocalize({
            defaultMessage: oktaError.backend_error,
            description: {
              text: "user get error while signin with Okta, they can try again or use email and password combination",
            },
          })
        )
      );
    }
  }, [oktaSignInError, strictLocalize]);

  const { userClickedSSOButton, reset: resetSSO, isActive } = useSSOStore();
  const { setUserHasPassword } = useSSOPasswordStore();
  const { setProviders } = useSSOProviderOptionsStore();
  const { errorMessage, resetErrorMessage } = useSSOErrorStore();
  const assignSettingBatch9DirectSignUpOnSignIn =
    useAssignSettingBatch9DirectSignUpOnSignIn();
  const { faireDirectBrand, faireDirectBrandToken, faireDirectBrandAlias } =
    useFaireDirectBrand();

  const signUpParams = useQueryParams(SignUpQueryParameters.parse);

  const history = useHistory();
  const isSignUpSplitEnabled = useSettingSignUpSplit();
  const assignSettingSignUpSplit = useAssignSetting(NEXTJS_SIGNUP_SPLIT_250311);

  const saveIncompleteSignup = useCallback(async (email: string) => {
    const payload = ISignUpRequest.build({
      user: IUser.build({
        email_address: email,
      }),
    });
    try {
      await saveIncompleteSignupAttempt(payload);
    } catch (error) {
      // error handling be BE; background network task, user shouldn't know
      // this request failed
    }
  }, []);

  const handleSignUp = useCallback(
    async (email: string) => {
      const modalVariant = AppSignUpModalVariant.DEFAULT;
      const faireDirectParams = buildFaireDirectParams(
        signUpParams,
        modalVariant,
        faireDirectBrandToken,
        faireDirectBrand?.name,
        faireDirectBrandAlias
      );

      const query = {
        ...signUpParams,
        email: email.trim(),
        source: removeLocaleFromUrl(history.location.pathname),
        storeOpeningDate: undefined,
        ...faireDirectParams,
      };

      const redirectLocation: string = signUpPersonal(query);

      trackEvent(`sign-up-page/modal/submit-email/success`);
      await saveIncompleteSignup(email);
      if (isActive) {
        ssoCache.clear();
      }

      assignSettingSignUpSplit();
      if (isSignUpSplitEnabled) {
        getLocationOrThrow().href = redirectLocation;
      } else {
        history.push(redirectLocation);
      }
    },
    [
      faireDirectBrand?.name,
      faireDirectBrandAlias,
      faireDirectBrandToken,
      history,
      isActive,
      isSignUpSplitEnabled,
      assignSettingSignUpSplit,
      saveIncompleteSignup,
      signUpParams,
    ]
  );

  const onSignUp = async (email: string) => {
    resetSSO();
    try {
      await fetchHCaptchaToken(true);
      await handleSignUp(email);
    } catch (error) {
      emitDatadogError(error);
      setError(error as LoginError);
    }
  };
  const onContinue = async () => {
    if (email !== undefined) {
      try {
        const result = await handleEmailSubmit(
          email,
          strictLocalize,
          fetchHCaptchaToken,
          setProviders,
          setUserHasPassword,
          setIsLoading
        );
        setError(undefined);
        onComplete(result);
      } catch (error) {
        emitDatadogError(error);
        const loginError = error as LoginError;
        if (loginError.httpStatusCode === 404 && email) {
          assignSettingBatch9DirectSignUpOnSignIn();
        }

        setError(loginError);
      }
    }
  };

  const onSignIn = (user: IUser) => {
    onLogin(user);
    onComplete({
      nextView: View.LOADING,
      user,
    });
  };

  //TODO: ADD NEW MFA UI FOR SSO
  const onSelectMFA = (mfaOptions: ILoginResponse.IMfaOption[]) =>
    onComplete({
      nextView: View.SELECT_MFA_OPTION,
      email,
      mfaOptions,
    });

  const onMFA = (mfaDescription: string, mfaTitle: string) =>
    onComplete({
      nextView: View.ENTER_MFA,
      mfaDescription,
      mfaTitle,
    });

  const onConfirmPassword = (signUpSuggestions?: ISignUpSuggestions) =>
    onComplete({
      ...getPayloadForConfirmPassword(signUpSuggestions),
      nextView: View.CONFIRM_PASSWORD,
    });

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (errorMessage) {
      resetErrorMessage();
    }
    if (error) {
      setError(undefined);
    }
    setEmail(event.currentTarget.value);
  };

  const emailInputRef = React.useCallback(
    (element: HTMLInputElement | null) => {
      if (element) {
        // Handle case where user has entered text during SSR by updating state on hydration.
        setEmail(element.value);
      }
    },
    [setEmail]
  );

  const isSSR = useIsSSR();

  return (
    <LoginWrapper>
      {isResetPasswordSuccess ? (
        <AnimatedTextWrapper>
          <StyledText>
            <Typography color={Core.text.inverse}>
              {strictLocalize({
                defaultMessage: "Your password has been changed.",
                description: {
                  text: "Indicates to the user that they successfully reset their password.",
                },
              })}
            </Typography>
          </StyledText>
        </AnimatedTextWrapper>
      ) : null}
      <Typography variant="displaySSerifRegular">{signInTitle}</Typography>
      {userClickedSSOButton && errorMessage ? (
        <>
          <Spacer height={4} />
          <Typography
            color={Core.text.critical}
            role="alert"
            align={{ mobileAndAbove: "center", desktopAndAbove: "start" }}
          >
            {errorMessage}
          </Typography>
          <Spacer height="1x" />
        </>
      ) : (
        <Spacer height="3x" />
      )}

      <Input
        autoFocus
        autoComplete="email"
        label={strictLocalize({
          defaultMessage: "Email address",
          description: {
            text: "Title for user's email address",
            img: "https://cdn.faire.com/fastly/0c15ccbd42cd5ef092908073ff6d97566178febed8721a520ad89b0bf9cd94ea.png",
          },
        })}
        aria-label={strictLocalize({
          defaultMessage: "Email",
          description: { text: "Accessible name for user's email address" },
        })}
        name="email"
        value={email}
        onChange={handleInputChange}
        onBlur={(e) => {
          trackFieldValue("emailaddress", e.currentTarget.value);
        }}
        onKeyDown={handleKeyDown(onContinue)}
        data-test-id="email"
        ref={emailInputRef}
      />
      {error ? (
        <div style={{ marginTop: "-4px" }}>
          <ErrorNotValidEmail error={error} email={email} onSignUp={onSignUp} />
          <Spacer size={12} />
        </div>
      ) : (
        <Spacer size="3x" />
      )}
      <ContinueToNextViewButton
        onClick={onContinue}
        disabled={isSSR}
        isLoading={isLoading}
      />
      {showSSOButtonsOnSignIn() ? (
        <>
          <SocialSignOnButtons
            onSignIn={onSignIn}
            onSelectMFA={onSelectMFA}
            onMFA={onMFA}
            onConfirmPassword={onConfirmPassword}
            hideApple={hideApple}
            sourceId="signin_modal_email_view"
            isOnSignIn
          />
        </>
      ) : null}
    </LoginWrapper>
  );
};

const handleEmailSubmit = async (
  email_address: string,
  strictLocalize: StrictLocalizeFunction,
  fetchHCaptchaToken: (
    shouldPostTokenToBackend: boolean
  ) => Promise<ILoginRequest.IHCaptchaData>,
  setSSOProviders: (providers: SocialSignOnProvider[]) => void,
  setUserHasPassword: (hasPassword: boolean) => void,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
): Promise<HandleEmailSubmitResult> => {
  if (!email_address) {
    throw new LoginError(
      strictLocalize({
        defaultMessage: "Please enter your email to continue.",
        description: {
          text: "The user did not enter an email address",
        },
      })
    );
  }

  const validEmail = isValidEmail(email_address);
  if (!validEmail) {
    trackRetailerPortalLoginModalEnterEmail("NOT_EMAIL");
    throw new LoginError(
      strictLocalize({
        defaultMessage: "Enter a valid email to continue.",
        description: {
          text: "The email address is not valid, advice on use a valid email",
        },
      })
    );
  }

  try {
    setIsLoading(true);
    const {
      available_social_providers,
      missing_password,
      partial_alias,
      user_token,
      mfa_title,
      mfa_description,
      mfa_options,
    } = await fetchHCaptchaToken(true).then(() =>
      userLoginData(
        ILoginDataRequest.build({
          email_address,
          enable_mfa_method_selection: true,
          trigger_m_f_a: true,
        })
      )
    );
    const resultWithMFA = handleMFAOnEmailSubmit(
      mfa_title,
      mfa_description,
      mfa_options,
      email_address,
      user_token
    );
    if (resultWithMFA) {
      trackSigninTriggersMfaSubmit("EMAIL");
      return resultWithMFA;
    }
    let nextView = View.ENTER_PASSWORD;
    if (
      (available_social_providers?.length ?? 0) > 0 &&
      showSSOButtonsOnSignIn()
    ) {
      if (available_social_providers.includes(SocialSignOnProvider.OKTA)) {
        trackRetailerPortalLoginModalEnterEmail("ADMIN");
        nextView = View.SSO;
      } else if (missing_password) {
        trackRetailerPortalLoginModalEnterEmail("SSO");
        nextView = View.SSO;
      } else {
        nextView = View.ENTER_PASSWORD;
      }
      setSSOProviders(
        available_social_providers.map(
          (provider) => SocialSignOnProvider[provider]
        )
      );
      setUserHasPassword(!missing_password);
    } else if (missing_password) {
      trackRetailerPortalLoginModalEnterEmail("CODE");
      nextView = View.ENTER_CODE;
    }
    const userToken = user_token;

    const obfuscatedEmail = partial_alias;

    if (nextView === View.ENTER_CODE) {
      await login(ILoginRequest.build({ email_address }));

      return {
        nextView,
        userToken,
        obfuscatedEmail,
        email: email_address,
      };
    }

    return {
      nextView,
      userToken,
      obfuscatedEmail,
      email: email_address,
    };
  } catch (e) {
    throw convertErrorToLoginError(e, strictLocalize);
  } finally {
    setIsLoading(false);
  }
};

const handleMFAOnEmailSubmit = (
  mfaTitle: string | undefined,
  mfaDescription: string | undefined,
  mfaOptions: Array<ILoginResponse.IMfaOption>,
  emailAddress: string,
  userToken: string | undefined
) => {
  if (!mfaTitle || !mfaDescription || !userToken) {
    return undefined;
  }
  if (mfaOptions.length > 1) {
    return {
      nextView: View.SELECT_MFA_OPTION,
      mfaTitle: mfaTitle,
      mfaDescription: mfaDescription,
      mfaOptions: mfaOptions,
      email: emailAddress,
      isMFAFromEmail: true,
    };
  }
  return {
    nextView: View.ENTER_MFA,
    mfaTitle: mfaTitle,
    mfaDescription: mfaDescription,
    userToken: userToken,
    email: emailAddress,
    isMFAFromEmail: true,
  };
};

const fadeInOut = keyframes`
    0% {
      opacity: 0;
      transform: translate(0px, -10px);
    }
    20%, 90% {
      opacity: 1;
      transform: translate(0px, -10px);
    }
    100% {
      opacity: 0;
      transform: translate(0px, -20px);
    }
  `;

const AnimatedTextWrapper = styled.div`
  opacity: 1;
  width: 100%;
  transform: translate(0px, -10px);
  animation: ${fadeInOut} 2200ms linear forwards;
`;

const StyledText = styled.div`
  background-color: ${Core.text.default};
  padding: 16px;
  border-radius: 8px;
  max-width: 315px;
  margin: 0 auto;
  text-align: center;
`;
